/**
 * Where every node is partway between the arrangement it had and the one it is getting.
 *
 * A hard cut between two layouts throws away the one thing a reader needs to keep their place: that
 * the component they were looking at is still there. Moving it instead is what carries identity
 * across a change of context or of grouping - the mark that travels is the same mark.
 *
 * Everything here is position, and that is a deliberate limit rather than a first draft. Scaling a
 * mark in would mean a per-node radius, which the instance packing does not take and the renderer
 * would have to learn; interpolating a colour would mean deciding what a component changing type
 * mid-flight means. Position alone needs neither, and answers the question the animation is for.
 *
 * What it does *not* do is make an unstable layout meaningful. Interpolating between two
 * arrangements a force solve produced from different seeds is motion with nothing behind it - the
 * marks travel a long way to say nothing. This is worth turning on for the layouts that are
 * determined by their data, which is the closed-form ones.
 */

import type { Link } from '../graph/topology';
import type { Vec3 } from '../types';

export type TransitionPositionsArgs = {
  /**
   * Where each thing was, by match key. Keys absent from the target are simply not drawn.
   *
   * Keyed on `NetworkNode.matchKey` rather than on the node id, which is what lets a mark be
   * recognised after a grouping renames it. Several target nodes may share one key - a cloned
   * component - and they all start from the one place it was, which animates as a split.
   */
  from: ReadonlyMap<string, Vec3>;
  /** The match keys being drawn now, parallel to `toPositions`. */
  toIds: readonly string[];
  toPositions: readonly Vec3[];
  /** Index-space edges over the target, for placing a node that has no previous position. */
  links: readonly Link[];
  /** 0 is entirely the old arrangement, 1 entirely the new. */
  progress: number;
};

/**
 * Smoothstep, which is the cheapest curve that leaves and arrives at rest.
 *
 * Linear interpolation reads as a machine moving something. The two zero derivatives at the ends
 * are what make it read as the graph settling instead - and the eye is far more sensitive to the
 * stop than to anything in the middle.
 */
const ease = (progress: number): number => {
  const clamped = Math.min(1, Math.max(0, progress));
  return clamped * clamped * (3 - 2 * clamped);
};

const lerp = (from: Vec3, to: Vec3, amount: number): Vec3 => [
  from[0] + (to[0] - from[0]) * amount,
  from[1] + (to[1] - from[1]) * amount,
  from[2] + (to[2] - from[2]) * amount,
];

/**
 * Where a node with no previous position comes in from.
 *
 * The mean of wherever its neighbours were, so a new component arrives from among the things it
 * connects to rather than from the middle of the picture or from nowhere. That is the honest
 * reading: it appeared *here*, in this part of the estate.
 *
 * Falls back to the centre of what was already on screen when none of its neighbours were - a node
 * whose whole neighbourhood is also new has no better claim on any particular direction, and coming
 * in from the middle at least reads as arriving rather than as being switched on.
 */
const toEntryPosition = (
  index: number,
  neighbours: ReadonlyMap<number, number[]>,
  toIds: readonly string[],
  from: ReadonlyMap<string, Vec3>,
  fallback: Vec3
): Vec3 => {
  const known = (neighbours.get(index) ?? [])
    .map(neighbour => from.get(toIds[neighbour]))
    .filter((position): position is Vec3 => position !== undefined);

  if (known.length === 0) return fallback;

  return [
    known.reduce((total, [x]) => total + x, 0) / known.length,
    known.reduce((total, [, y]) => total + y, 0) / known.length,
    known.reduce((total, [, , z]) => total + z, 0) / known.length,
  ];
};

const toNeighbours = (links: readonly Link[]): Map<number, number[]> => {
  const neighbours = new Map<number, number[]>();
  const add = (at: number, other: number) => {
    const existing = neighbours.get(at);
    if (existing) {
      existing.push(other);
      return;
    }
    neighbours.set(at, [other]);
  };

  for (const { source, target } of links) {
    add(source, target);
    add(target, source);
  }
  return neighbours;
};

/** The middle of the previous arrangement, or the origin when there was not one. */
const toPreviousCentre = (from: ReadonlyMap<string, Vec3>): Vec3 => {
  if (from.size === 0) return [0, 0, 0];

  let x = 0;
  let y = 0;
  let z = 0;
  for (const [px, py, pz] of from.values()) {
    x += px;
    y += py;
    z += pz;
  }
  return [x / from.size, y / from.size, z / from.size];
};

export const toTransitionPositions = ({
  from,
  toIds,
  toPositions,
  links,
  progress,
}: TransitionPositionsArgs): Vec3[] => {
  const amount = ease(progress);
  if (amount >= 1 || from.size === 0) return [...toPositions];

  const neighbours = toNeighbours(links);
  const centre = toPreviousCentre(from);

  return toPositions.map((target, index) => {
    const previous =
      from.get(toIds[index]) ??
      toEntryPosition(index, neighbours, toIds, from, centre);

    return lerp(previous, target, amount);
  });
};

/**
 * The positions of a drawn arrangement, by match key, which is what the next movement starts from.
 *
 * Several marks can share a key, and the mean of them is what the key was at. That is the merge
 * case - an ungrouping folds a component's copies back into one mark - and a single mark can only
 * come from one place, so it comes from the middle of where its copies were.
 */
export const toPositionsById = (
  ids: readonly string[],
  positions: readonly Vec3[]
): Map<string, Vec3> => {
  const sums = new Map<string, { total: Vec3; count: number }>();

  ids.forEach((id, index) => {
    const position = positions[index];
    if (position === undefined) return;

    const existing = sums.get(id);
    if (existing) {
      existing.total = [
        existing.total[0] + position[0],
        existing.total[1] + position[1],
        existing.total[2] + position[2],
      ];
      existing.count += 1;
      return;
    }
    sums.set(id, { total: position, count: 1 });
  });

  return new Map(
    [...sums].map(([id, { total, count }]): [string, Vec3] => [
      id,
      [total[0] / count, total[1] / count, total[2] / count],
    ])
  );
};

/**
 * How much of the new arrangement was already on screen, 0 to 1.
 *
 * A movement between two arrangements with nothing in common is not a movement. Every mark would
 * come in from the centre of the old picture at once, which reads as an explosion rather than as a
 * re-arrangement - and says something false, that these marks came from there. Below a threshold
 * the caller should cut instead.
 */
export const toMatchRatio = (
  from: ReadonlyMap<string, Vec3>,
  toIds: readonly string[]
): number =>
  toIds.length === 0
    ? 0
    : toIds.filter(id => from.has(id)).length / toIds.length;

/**
 * How far the marks actually travel, as a fraction of the picture's own size.
 *
 * The median rather than the mean or the max, because a handful of marks crossing the view while
 * everything else stays put is not a re-arrangement - it is a re-arrangement of a handful of
 * marks, and the picture as a whole has not moved.
 *
 * The caller needs this because a movement nobody can see still costs what a movement costs. When
 * one component leaves a grouped estate of a few hundred, the packing puts almost everything back
 * within a percent of where it was - so the animation carries no information and the only thing a
 * reader perceives is its side effects.
 */
export const toMovementRatio = ({
  from,
  toIds,
  toPositions,
}: {
  from: ReadonlyMap<string, Vec3>;
  toIds: readonly string[];
  toPositions: readonly Vec3[];
}): number => {
  const moves = toIds
    .map((id, index) => {
      const previous = from.get(id);
      const target = toPositions[index];
      if (previous === undefined || target === undefined) return null;
      return Math.hypot(
        target[0] - previous[0],
        target[1] - previous[1],
        target[2] - previous[2]
      );
    })
    .filter((move): move is number => move !== null)
    .sort((left, right) => left - right);

  if (moves.length === 0) return 0;

  /* Swept rather than spread. `Math.max(...positions)` past roughly 125k arguments is a
     `RangeError` rather than a wrong answer, and this runs over every drawn mark - so a large
     estate would throw here, inside the render that was about to animate it. */
  let extent = 0;
  for (const axis of [0, 1, 2]) {
    let lowest = Infinity;
    let highest = -Infinity;
    for (const position of toPositions) {
      lowest = Math.min(lowest, position[axis]);
      highest = Math.max(highest, position[axis]);
    }
    extent = Math.max(extent, highest - lowest);
  }
  if (extent <= 0) return 0;

  return moves[Math.floor(moves.length / 2)] / extent;
};
